import axios from 'axios';
import Cookies from 'js-cookie';
import { useMutation } from 'react-query';

// Helper: store user info to localStorage (adjust properties as needed)
const setUser = (access_token) => {
  // Here you can decode the token or store additional information if needed.
  localStorage.setItem(
    'user',
    JSON.stringify({ type: 'verified', token: access_token }),
  );
};

const login = async ({ username, password }) => {
  try {
    // Clear previous cookies and user data
    Cookies.remove('access_token');
    Cookies.remove('refresh_token');
    localStorage.removeItem('user');

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/auth/login`,
      { username, password },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    const { access_token, refresh_token } = response.data;
    const sevenDaysFromNow = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);

    // Set cookies with expiration date
    Cookies.set('access_token', access_token, {
      expires: sevenDaysFromNow,
      secure: true,
      sameSite: 'Strict',
    });
    Cookies.set('refresh_token', refresh_token, {
      expires: sevenDaysFromNow,
      secure: true,
      sameSite: 'Strict',
    });

    // Store user info in localStorage for later use (e.g., by Dixa Messenger)
    setUser(access_token);

    return response;
  } catch (err) {
    return err.response;
  }
};

const logout = async () => {
  // Kill any active Dixa session if available
  if (window._dixa_ && typeof window._dixa_.invoke === 'function') {
    window._dixa_.invoke('destroy');
  }

  const access_token = Cookies.get('access_token');
  // Remove cookies and user info
  Cookies.remove('access_token');
  Cookies.remove('refresh_token');
  localStorage.removeItem('user');
  // Remove Dixa messenger user identity from local storage
  localStorage.removeItem('dixa-messenger-user-identity');
  localStorage.removeItem('dixa-messenger-session-token');

  await axios.post(
    `${process.env.REACT_APP_API_URL}/auth/logout`,
    {},
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
      },
    },
  );

  // Reload the window after a brief delay to finalize logout
  setTimeout(() => {
    window.location.reload();
  }, 500);
};

const extractToken = (token) => {
  try {
    const tokenParts = token.split('.');
    const decodedPayload = JSON.parse(atob(tokenParts[1]));
    return decodedPayload;
  } catch (error) {
    console.error('Invalid token format');
    return null;
  }
};

const useLoginMutation = () => {
  return useMutation(login, {
    onSuccess: (data) => {
      if (data.data && data.data.access_token && data.data.refresh_token) {
        // Navigate to the orders page.
        window.location.href = '/orders/AWAITING';
        // Force a reload after a short delay to ensure all components (including Dixa) reinitialize.
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    },
  });
};

const useLogoutMutation = () => {
  return useMutation(logout);
};

const sendOTPEmail = async (email) => {
  return axios.post(
    `${process.env.REACT_APP_API_URL}/auth/otp/email/send`,
    { email },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
};

const sendOTPPhone = async ({ phone, countryData }) => {
  const dialCode = countryData.match(/\+\d+/)?.[0] || '';
  return axios.post(
    `${process.env.REACT_APP_API_URL}/auth/otp/phone/send`,
    { phone: `${dialCode}${phone}` },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
};

const verifyOTPEmail = async ({ email, code }) => {
  const response = await axios.post(
    `${process.env.REACT_APP_API_URL}/auth/otp/email/check`,
    { email, code },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  const { access_token, refresh_token } = response.data;
  const sevenDaysFromNow = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);

  // Set cookies with expiration date
  Cookies.set('access_token', access_token, {
    expires: sevenDaysFromNow,
    secure: true,
    sameSite: 'Strict',
  });
  Cookies.set('refresh_token', refresh_token, {
    expires: sevenDaysFromNow,
    secure: true,
    sameSite: 'Strict',
  });

  // Save user info for later use
  setUser(access_token);

  return response.data;
};

const verifyOTPhone = async ({ phone, code }) => {
  const response = await axios.post(
    `${process.env.REACT_APP_API_URL}/auth/otp/phone/check`,
    { phone, code },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  const { access_token, refresh_token } = response.data;
  const sevenDaysFromNow = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);

  // Set cookies with expiration date
  Cookies.set('access_token', access_token, {
    expires: sevenDaysFromNow,
    secure: true,
    sameSite: 'Strict',
  });
  Cookies.set('refresh_token', refresh_token, {
    expires: sevenDaysFromNow,
    secure: true,
    sameSite: 'Strict',
  });

  // Save user info for later use
  setUser(access_token);

  return response.data;
};

const addPassword = async ({ password, access_token, email, phone }) => {
  try {
    await axios.post(
      `${process.env.REACT_APP_API_URL}/auth/password/set`,
      { password },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${access_token}`,
        },
      },
    );

    return { success: true };
  } catch (error) {
    console.error('Error setting password:', error);
    throw error;
  }
};

const useAddPasswordMutation = () => {
  const mutation = useMutation(addPassword);

  return {
    mutation,
    createCustomer: async ({ email, phone, access_token, customerType }) => {
      try {
        const customerResponse = await axios.post(
          `${process.env.REACT_APP_API_URL}/customers?accessToken=${access_token}`,
          {
            email,
            phone,
            customerType,
          },
        );

        console.log('Customer data sent:', customerResponse.data);
        window.location.href = '/onboarding';
      } catch (error) {
        console.error('Error creating customer:', error);
        throw error;
      }
    },
  };
};

const resetPassword = async ({
  access_token,
  currentPassword,
  newPassword,
}) => {
  try {
    await axios.post(
      `${process.env.REACT_APP_API_URL}/auth/password/update`,
      { currentPassword, newPassword },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${access_token}`,
        },
      },
    );

    return { success: true };
  } catch (error) {
    console.error('Error resetting password:', error);
    throw error;
  }
};

const resetPasswordMutation = async ({
  access_token,
  currentPassword,
  newPassword,
}) => {
  try {
    await resetPassword({ access_token, currentPassword, newPassword });
    return { success: true };
  } catch (error) {
    console.error('Error resetting password:', error);
    throw error;
  }
};

const useResetPasswordMutation = () => {
  return useMutation(resetPasswordMutation);
};

const useSendOTPEmailMutation = () => {
  return useMutation(sendOTPEmail);
};

const useSendOTPPhoneMutation = () => {
  return useMutation(sendOTPPhone);
};

const useVerifyOTPEmailMutation = () => {
  return useMutation(verifyOTPEmail);
};

const useVerifyOTPPhoneMutation = () => {
  return useMutation(verifyOTPhone);
};

export {
  useLoginMutation,
  useLogoutMutation,
  useSendOTPEmailMutation,
  useSendOTPPhoneMutation,
  useVerifyOTPEmailMutation,
  useVerifyOTPPhoneMutation,
  addPassword,
  useAddPasswordMutation,
  useResetPasswordMutation,
  extractToken,
};
